import { useAlarms } from '@ctcells/api'
import type { Alarm } from '@ctcells/contract'
import { dataSourceFor, type NavItemId } from '@ctcells/core'
import { t } from '@ctcells/i18n'

import { ALARM_SNAPSHOT_ARGS, ALARM_PAGE_LIMIT } from '../screens/alarms/alarmsQuery.ts'
import { DEMO_ALARMS } from '../screens/demoData.ts'

import type { NavBadge } from './ConsoleHeader.tsx'

/**
 * 헤더 메뉴의 **활성 알람 배지** — 셸이 쥔다.
 *
 * ## 왜 셸인가
 *
 * `ConsoleHeader` 에 `badges` 프롭과 `nav.alarmBadge` 문구가 2026-09-18부터 있었는데
 * **아무도 채우지 않았다.** 그래서 활성 알람이 화면 어디에도 안 보였다 — 알람 탭을 직접
 * 열어야만 알 수 있었다. 헤더는 라우트와 무관하게 계속 떠 있으므로 **화면이 아니라 셸**이
 * 이 값을 쥔다(계정 영역 `useNavAccount` 와 같은 결).
 *
 * ## 어느 메뉴에 붙나 — 「알람」
 *
 * 🔴 **2026-09-22 에 「작업」에서 「알람」으로 옮겼다**(eric 지시 「작업에서 알람탭을 따로 떼줘」).
 *    알람은 2026-09-18~09-22 동안만 「작업」 문 안의 탭이었고, 지금은 헤더의 **제 문**이다
 *    (`NAV_GROUPS` 의 `alarms`). 배지는 **그 화면으로 가는 문**에 붙는다 — 문이 옮겨 가면
 *    배지도 같이 옮긴다. 둘이 갈리면 배지가 아무 데도 안 붙거나 엉뚱한 문에 붙는다.
 * ⚠ 장비 관점 콘솔은 사이드바에 알람 항목이 따로 있고 **제 셸이 쥔다** — 이 훅을 쓰지 않는다.
 *
 * ## 자료 — 알람 화면이 쓰는 «그 질의»다
 *
 * 🔴 **왕복을 늘리지 않는다.** `useAlarms(ALARM_SNAPSHOT_ARGS)` 는 `AlarmsContainer` 와
 *    **같은 인자 = 같은 캐시 키**라, 알람 화면을 열어도 요청이 더 나가지 않는다.
 * 🔴 **주기 폴링이 아니다**(DP-01). 갱신은 구독이 한다 — `stream/bridge.ts` 가 `alarm`
 *    프레임에 `keys.alarms.all()` 을 무효화하고, 그때 배지와 화면이 **함께** 다시 그려진다.
 * ⚠ 서버에 `state=active` 축이 있지만 쓰지 않는다. 좁혀 받으면 캐시가 갈려 왕복이 는다
 *   (`alarmsQuery.ts` 머리말).
 *
 * 🔴 **지금은 그 프레임이 «안 온다» — 서버 쪽 구멍이다**(실측 2026-09-22 · **BR-57**).
 *    `/events/stream` 100초 탭에서 `alarm` **0건**인데 같은 창에 `GET /alarms` 는 5건 늘었다.
 *    모듈이 세운 알람(`core/module_subscriber` → `raise_alarm` 직접 호출)은 중계(`stream_relay`)
 *    를 안 타서 발행 자리에 닿지 않는다. ⇒ **떠 있는 화면의 배지가 늦는다.**
 *    ⚠ **폴링으로 메우지 마라**(DP-01). 다리는 이미 걸려 있어 **프레임만 오면 그 자리에서
 *      고쳐진다** — 여기 `refetchInterval` 을 넣으면 그 구멍이 영영 안 보인다.
 *
 * ## 🔴 부가 조회다 — 실패해도 헤더·화면은 산다
 *
 * `GET /alarms` 는 서버가 **세 역할 전부**에게 연다(`app/views/alarms.py:131 ROLES` —
 * `packages/ui/src/test/roleAccess.ts` 의 전수표). 그래도 403·네트워크 실패가 나면
 * **배지를 안 그린다.** 「못 봤다」를 「0건」으로 접지도, 그 사유로 헤더를 죽이지도 않는다.
 * 잠긴 메뉴에 배지를 안 다는 판단은 `ConsoleHeader` 가 따로 한다(못 여는 화면의 건수는
 * 알릴 값이 아니다).
 */

/**
 * 배지가 붙는 메뉴. 🔴 **알람 화면으로 가는 문(`NAV_GROUPS` 의 그 항목)에서 온 값**이고,
 * 그 둘이 갈리면 `useAlarmBadge.test.tsx` 가 잡는다 — 여기 문자열만 바꾸지 마라.
 */
export const ALARM_BADGE_NAV_ID: NavItemId = 'alarms'

/** 위험색을 켜는 심각도. 🔴 서버 어휘(`Alarm.severity`)의 부분집합이다 — 새 값을 만들지 않는다 */
const SEVERE: readonly string[] = ['ERROR', 'CRITICAL']

/**
 * 「활성」의 뜻 — **서버가 쓰는 그 낱말이다**(`GET /alarms?state=active` 의 그 값).
 * ⚠ `acknowledged`(확인했지만 아직 안 해제)를 여기 더하지 마라. 그러면 사람이 [확인]을
 *   눌러도 수가 안 줄어 **눌러도 아무 일도 안 일어나는 것처럼** 보인다. 그 수를 같이 보고
 *   싶으면 축을 하나 더 만들 일이지 이 수에 섞을 일이 아니다.
 */
function isActive(alarm: Alarm): boolean {
  return alarm.state === 'active'
}

/**
 * 목록 한 벌 → 배지 하나. **순수 함수라 단독으로 시험한다.**
 *
 * @param complete 받은 목록이 전부인가(`items.length >= total_count`). 아니면 「최소 N건」이다
 */
export function alarmBadgeOf(alarms: readonly Alarm[], complete: boolean): NavBadge | undefined {
  const active = alarms.filter(isActive)
  /* 🔴 0 이면 배지가 아예 없다 — 「0」을 그리면 알림이 있는 것처럼 보인다 */
  if (active.length === 0) return undefined

  const danger = active.some((alarm) => alarm.severity != null && SEVERE.includes(alarm.severity))
  const count = active.length
  const base = complete
    ? t('nav.alarmBadge', { count })
    : /* 🔴 덜 받았으면 그 수는 하한이다. 완전한 수인 척 적지 않는다 */
      t('nav.alarmBadgeAtLeast', { count })

  return {
    count,
    tone: danger ? 'danger' : 'neutral',
    label: danger ? `${base} · ${t('nav.alarmBadgeSevere')}` : base,
  }
}

/** `ConsoleHeader` 의 `badges` 프롭 한 벌. 없으면 **키 자체가 없다** */
export type NavBadges = Partial<Record<string, NavBadge>>

export function useAlarmBadges(): NavBadges {
  const demo = dataSourceFor('alarms') === 'demo'
  /*
   * 🔴 훅은 조건 없이 부른다 — 끄는 것은 `skip` 이다(`activeBatchSource.ts` 와 같은 규율).
   * ⚠ live 에서는 **`ALARM_SNAPSHOT_ARGS` 그대로**를 넘겨야 한다. `{ ...args, skip: false }`
   *   로 지으면 캐시 키가 갈려 알람 화면과 목록을 두 벌 받는다.
   */
  const query = useAlarms(demo ? { limit: ALARM_PAGE_LIMIT, skip: true } : ALARM_SNAPSHOT_ARGS)

  if (demo) {
    const badge = alarmBadgeOf(DEMO_ALARMS, true)
    return badge === undefined ? {} : { [ALARM_BADGE_NAV_ID]: badge }
  }

  /* 🔴 못 봤으면 안 그린다 — 조회 중(`undefined`)도, 실패(403·끊김)도 같은 처분이다 */
  const items = query.data?.items
  if (query.isError || items === undefined) return {}

  const total = query.data?.total_count ?? null
  const badge = alarmBadgeOf(items, total === null || items.length >= total)
  return badge === undefined ? {} : { [ALARM_BADGE_NAV_ID]: badge }
}
